import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import ArrowDownOutlined from '@ant-design/icons/ArrowDownOutlined';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import type { RunRefusal } from '@/api/runs';
import type { DocumentLink } from '@/utils/messageParser';
import type { ConvMessage } from '../types';
import AnswerItem, { type AnswerItemProps } from './AnswerItem';
import QuestionItem from './QuestionItem';
import { numberMessages } from './useConversation';
import { useFollowLatest } from './useFollowLatest';
import styles from './conversation.module.css';

export interface ConversationHandle {
  /** Scroll to the newest message (after the user sends). */
  jumpToLatest: () => void;
}

interface ConversationViewProps {
  inquiryId: string | undefined;
  messages: ConvMessage[];
  loaded: boolean;
  /** The message list could not be loaded. */
  failed: boolean;
  onRetryLoad: () => void;
  links: DocumentLink[];
  /** A run is active (or being sent) in this Q&A. */
  busy: boolean;
  followLatest: boolean;
  /** Title and guidance of an empty Q&A. */
  empty: { title: string; body: string };
  editConditions: (message: ConvMessage) => string;
  /** See AnswerItem. */
  unrecordedModeNote: string;
  onStop: (runId: string) => void;
  onReconnect: (runId: string) => void;
  /** Resolves to the refusal when the server refused it. */
  onRetry: (message: ConvMessage) => Promise<RunRefusal | null>;
  onAlternative: (message: ConvMessage) => Promise<RunRefusal | null>;
  /** Resolves to whether the edited question was sent. */
  onResubmit: (message: ConvMessage, text: string) => Promise<boolean>;
  onCopyFailed: (text: string) => void;
  onCopyAnswer: AnswerItemProps['onCopy'];
  onExportAnswer: AnswerItemProps['onExport'];
}

/** The questions and answers of the open Q&A, oldest at the top. */
const ConversationView = forwardRef<ConversationHandle, ConversationViewProps>(
  (
    {
      inquiryId,
      messages,
      loaded,
      failed,
      onRetryLoad,
      links,
      busy,
      followLatest,
      empty,
      editConditions,
      unrecordedModeNote,
      onStop,
      onReconnect,
      onRetry,
      onAlternative,
      onResubmit,
      onCopyFailed,
      onCopyAnswer,
      onExportAnswer,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const box = useRef<HTMLDivElement>(null);
    const content = useRef<HTMLDivElement>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [refusals, setRefusals] = useState<Record<string, RunRefusal>>({});

    const scroll = useFollowLatest({
      box,
      content,
      inquiryId,
      ready: loaded && !failed,
      followLatest,
    });

    useImperativeHandle(ref, () => ({ jumpToLatest: scroll.jumpToLatest }), [
      scroll.jumpToLatest,
    ]);

    const numbers = numberMessages(messages);
    const numberOf = (id: string | number | null | undefined) =>
      id == null ? null : (numbers.get(String(id)) ?? null);

    const resubmitWith = useCallback(
      (send: (message: ConvMessage) => Promise<RunRefusal | null>) =>
        async (message: ConvMessage) => {
          const key = String(message.id);
          setRefusals(({ [key]: _, ...rest }) => rest);
          const refusal = await send(message);
          if (refusal)
            setRefusals(previous => ({ ...previous, [key]: refusal }));
        },
      [],
    );
    const retry = useMemo(() => resubmitWith(onRetry), [onRetry, resubmitWith]);
    const alternative = useMemo(
      () => resubmitWith(onAlternative),
      [onAlternative, resubmitWith],
    );

    const startEdit = useCallback((message: ConvMessage) => {
      setEditingId(String(message.id));
    }, []);
    const cancelEdit = useCallback(() => setEditingId(null), []);

    return (
      <div className={styles.viewport}>
        <div
          ref={box}
          className={styles.scroller}
          onScroll={scroll.onScroll}
          data-testid="conversation"
        >
          <div ref={content} className={styles.thread}>
            {!loaded && inquiryId ? (
              <div className={styles.loading}>
                <Spin aria-label={t('v2.common.loading')} />
              </div>
            ) : null}
            {loaded && failed ? (
              <div className={styles.loadFailed} role="alert">
                <span>{t('v2.messages.loadFailed')}</span>
                <button
                  type="button"
                  className={styles.action}
                  onClick={onRetryLoad}
                  data-testid="conversation-retry"
                >
                  {t('v2.common.retry')}
                </button>
              </div>
            ) : null}
            {loaded && !failed && messages.length === 0 ? (
              <div className={styles.empty} data-testid="conversation-empty">
                <h2 className={styles.emptyTitle}>{empty.title}</h2>
                <p className={styles.emptyBody}>{empty.body}</p>
              </div>
            ) : null}
            {loaded
              ? messages.map(message => (
                  <div key={message.id} className={styles.turn}>
                    <QuestionItem
                      message={message}
                      number={numberOf(message.id)}
                      parentNumber={numberOf(message.parent_message_id)}
                      busy={busy}
                      editing={editingId === String(message.id)}
                      editConditions={editConditions(message)}
                      onStartEdit={startEdit}
                      onCancelEdit={cancelEdit}
                      onResubmit={onResubmit}
                      onCopyFailed={onCopyFailed}
                    />
                    <AnswerItem
                      message={message}
                      number={numberOf(message.id)}
                      parentNumber={numberOf(message.parent_message_id)}
                      busy={busy}
                      links={links}
                      refusal={refusals[String(message.id)] ?? null}
                      unrecordedModeNote={unrecordedModeNote}
                      onStop={onStop}
                      onRetry={retry}
                      onAlternative={alternative}
                      onReconnect={onReconnect}
                      onEdit={startEdit}
                      onCopy={onCopyAnswer}
                      onExport={onExportAnswer}
                    />
                  </div>
                ))
              : null}
          </div>
        </div>
        {scroll.away ? (
          <button
            type="button"
            className={scroll.hasNew ? styles.jumpNew : styles.jump}
            onClick={scroll.jumpToLatest}
            data-testid="jump-to-latest"
          >
            <ArrowDownOutlined aria-hidden />
            {scroll.hasNew
              ? t('v2.messages.newContent')
              : t('v2.messages.jumpLatest')}
          </button>
        ) : null}
      </div>
    );
  },
);

ConversationView.displayName = 'ConversationView';

export default ConversationView;
